import clsx from "clsx";
import type { CSSProperties } from "react";

import { inputString, modelFallbackLines } from "@tsmono/inspect-common/utils";
import type { FilterType } from "@tsmono/inspect-components/columnFilter";
import {
  arrayToString,
  filename,
  formatCurrency,
  formatNumber,
  getOwn,
  valueAsString,
} from "@tsmono/util";

import { ScoreLabel } from "../../../app/types";
import { SampleSummary } from "../../../client/api/types";
import {
  kScoreTypeBoolean,
  kScoreTypeNumeric,
  kScoreTypePassFail,
} from "../../../constants";
import { formatDateTime, formatTime } from "../../../utils/format";
import { SamplesDescriptor } from "../../samples/descriptor/samplesDescriptor";
import {
  samplesOperatorsForKind,
  type SampleFilterSpecRegistry,
} from "../../samples/sample-tools/filterSpecRegistry";
import {
  deriveSampleStatus,
  SampleStatusIcon,
  statusSortValue,
} from "../../samples/status/status";
import {
  ColumnComparator,
  ExtendedColumnDef,
} from "../../shared/data-grid/columnTypes";
import { comparators } from "../gridComparators";

import { MarkdownCellDiv, ScoreCellDiv } from "./cells";
import {
  colorForValue,
  resolveScale,
  type WireScoreColorScale,
} from "./colorScale";
import styles from "./gridCells.module.css";
import { SampleRow } from "./types";

export type SampleGridViewMode = "list" | "grid";

// Value comparators for client-side sorting (deferred — kept on column meta
// for later wiring). `gridComparators` pins missing values last in both
// directions (`isDescending` flips the missing-value sentinel).
const numberCompare: ColumnComparator = (a, b, isDescending) =>
  comparators.number(a, b, isDescending);
const dateCompare: ColumnComparator = (a, b) => comparators.date(a, b);
const stringCompare: ColumnComparator = (a, b) =>
  valueAsString(a ?? "").localeCompare(valueAsString(b ?? ""));

export interface SampleGridContext {
  viewMode: SampleGridViewMode;
  multiLog: boolean;
  /** Single-log only — drives markdown rendering, score rendering, and
   *  initial visibility for `input`/`target`/`answer`. */
  descriptor?: SamplesDescriptor;
  /** Single-log only — score columns are emitted per-scorer in this
   *  mode. Visibility (which scores are currently selected) is layered
   *  on by the caller via the column-visibility map. */
  scores?: ScoreLabel[];
  epochs?: number;
  /** Cross-log only — used to discover all distinct score names. */
  samples?: SampleSummary[];
  /** Per-metric display label overrides, e.g. `{ "ascii-art": "ASCII
   *  Art" }`. Falls through to the raw metric name when a key isn't
   *  present. Sourced from the eval-author's `task_samples_view`. */
  scoreLabels?: Record<string, string>;
  /** Per-metric background colour scales, e.g. `{ "accuracy":
   *  "good-high", "verdict": { "yes": "bad" } }`. Numeric metrics use
   *  named palettes (gradient anchored at descriptor min/max);
   *  categorical metrics map values to semantic roles. Pass/fail and
   *  boolean metrics ignore this — their pills already carry the
   *  semantic. */
  scoreColorScales?: Record<string, WireScoreColorScale>;
  /** When true, score columns render compact: narrow widths with rotated
   *  45° headers so many scorers fit horizontally. Off by default;
   *  eval authors opt in via `task_samples_view.compact_scores`. */
  compactScores?: boolean;
  /** When set, only columns in the registry are filterable, with operator
   *  lists narrowed to what round-trips through the filtrex bridge
   *  (samples-tab mode). Absent → every column is filterable with default
   *  operators (cross-log mode). */
  filterSpecRegistry?: SampleFilterSpecRegistry;
}

type SampleColumn = ExtendedColumnDef<SampleRow>;

const EmptyCell = () => <div>-</div>;

export const SCORE_FIELD_RAW_PREFIX = "score_";
export const SCORE_FIELD_PER_SCORER_PREFIX = "score__";

export const perScorerFieldKey = (label: ScoreLabel): string =>
  `${SCORE_FIELD_PER_SCORER_PREFIX}${label.scorer}__${label.name}`;

const rawScoreFieldKey = (name: string): string =>
  `${SCORE_FIELD_RAW_PREFIX}${name}`;

const statusValue = (row: SampleRow): string => {
  const completed = row.completed ?? row.data?.completed;
  const error = row.error ?? row.data?.error;
  return statusSortValue(deriveSampleStatus(completed, error), error);
};

const inputText = (row: SampleRow): string => {
  if (row.input !== undefined) return row.input;
  if (row.data) return inputString(row.data.input).join(" ");
  return "";
};

const targetText = (row: SampleRow): string => {
  if (row.target !== undefined) return row.target;
  if (row.data?.target != null) return arrayToString(row.data.target);
  return "";
};

/** Build the superset of sample columns. Visibility is *not* applied here —
 *  the caller controls it via the DataGrid `columnVisibility` map. */
export function buildSampleColumns(
  ctx: SampleGridContext
): ExtendedColumnDef<SampleRow>[] {
  const { viewMode, multiLog, descriptor } = ctx;
  const isList = viewMode === "list";
  const shape = descriptor?.messageShape;

  const cols: SampleColumn[] = [];

  // # column — pinned-left index. Cross-log only (single-log doesn't benefit
  // from a separate index since rows are naturally ordered).
  if (multiLog) {
    cols.push({
      id: "displayIndex",
      header: "#",
      // 65 matches what the AG grid actually rendered (its declared 80 was
      // always squeezed by the initial fit-to-grid pass).
      size: 65,
      minSize: 50,
      maxSize: 80,
      enableSorting: false,
      enableResizing: false,
      pinned: "start",
      accessorFn: (row) => row.displayIndex,
      cell: ({ row }) => {
        const value = row.original.displayIndex;
        if (value === undefined) return "";
        return <div>{value}</div>;
      },
    });
  }

  // sample-level status icon (always). The id is a wire contract — eval
  // authors reference it as `TaskSamplesColumnId` "sampleStatus" from
  // `Task(viewer=...)` (see `viewer/_config.py`) — so it must not track
  // internal helper naming.
  cols.push({
    id: "sampleStatus",
    header: isList ? "" : "Sample Status",
    headerTitle: "Sample Status",
    size: isList ? 28 : 100,
    minSize: isList ? 28 : 80,
    enableResizing: false,
    accessorFn: (row) => statusValue(row),
    titleValue: (row) => {
      const completed = row.completed ?? row.data?.completed;
      const error = row.error ?? row.data?.error;
      return error ? error : deriveSampleStatus(completed, error);
    },
    cell: ({ row }) => {
      const item = row.original;
      const completed = item.completed ?? item.data?.completed;
      const error = item.error ?? item.data?.error;
      return <SampleStatusIcon status={deriveSampleStatus(completed, error)} />;
    },
  });

  // task / model / logFile / eval status — cross-log only.
  if (multiLog) {
    cols.push(
      {
        id: "status",
        header: "Eval Status",
        size: 110,
        minSize: 80,
        accessorFn: (row) => row.status,
        cell: ({ getValue }) => <div>{valueAsString(getValue() ?? "")}</div>,
      },
      {
        id: "task",
        header: "Task",
        size: 160,
        minSize: 100,
        flex: 1,
        accessorFn: (row) => row.task,
        cell: ({ getValue }) => <div>{valueAsString(getValue() ?? "")}</div>,
      },
      {
        id: "model",
        header: "Model",
        size: 160,
        minSize: 100,
        flex: 1,
        accessorFn: (row) => row.model,
        cell: ({ getValue }) => <div>{valueAsString(getValue() ?? "")}</div>,
      },
      {
        id: "logFile",
        header: "Log File",
        size: 200,
        minSize: 150,
        flex: 1,
        accessorFn: (row) => row.logFile,
        cell: ({ getValue }) => {
          const value = getValue<string | undefined>();
          return <div>{value ? filename(value) : ""}</div>;
        },
      },
      {
        id: "completed_at",
        header: "Completed",
        size: 140,
        minSize: 80,
        maxSize: 160,
        meta: { sortComparator: dateCompare },
        accessorFn: (row) => row.data?.completed_at ?? undefined,
        cell: ({ getValue }) => {
          const value = getValue<string | undefined>();
          return <div>{value ? formatDateTime(new Date(value)) : ""}</div>;
        },
      }
    );
  }

  // id (sample id)
  cols.push({
    id: "sampleId",
    header: "Id",
    size: shape ? Math.max(35, shape.idSize * 16) : 120,
    minSize: 35,
    accessorFn: (row) => String(row.sampleId),
    cell: ({ getValue }) => <div>{getValue<string>()}</div>,
  });

  // sample uuid — opt-in via column selector.
  cols.push({
    id: "sampleUuid",
    header: "UUID",
    size: 280,
    minSize: 80,
    accessorFn: (row) => row.data?.uuid ?? "",
    cell: ({ getValue }) => <div>{getValue<string>()}</div>,
  });

  // epoch
  cols.push({
    id: "epoch",
    header: "Epoch",
    size: 60,
    minSize: 40,
    meta: { align: "center", sortComparator: numberCompare },
    accessorFn: (row) => row.epoch,
    cell: ({ getValue }) => <div>{valueAsString(getValue() ?? "")}</div>,
  });

  // input
  cols.push({
    id: "input",
    header: "Input",
    size: 360,
    minSize: 240,
    flex: shape?.inputSize ? shape.inputSize : 3,
    accessorFn: (row) => inputText(row),
    cell: isList
      ? ({ row }) => (
          <MarkdownCellDiv
            semanticClass="sample-input"
            text={inputText(row.original)}
          />
        )
      : ({ getValue }) => <div>{getValue<string>()}</div>,
  });

  // target
  cols.push({
    id: "target",
    header: "Target",
    size: 200,
    minSize: 120,
    flex: shape?.targetSize ? shape.targetSize : 1,
    accessorFn: (row) => targetText(row),
    cell: isList
      ? ({ row }) => {
          const text = targetText(row.original);
          if (!text) return null;
          return (
            <MarkdownCellDiv
              semanticClass="sample-target"
              text={text}
              trimRenderedText
            />
          );
        }
      : ({ getValue }) => <div>{getValue<string>()}</div>,
  });

  // answer (only meaningful when descriptor present)
  if (descriptor) {
    cols.push({
      id: "answer",
      header: "Answer",
      size: 200,
      minSize: 120,
      flex: shape?.answerSize ? shape.answerSize : 1,
      accessorFn: (row) => row.answer ?? "",
      cell: isList
        ? ({ row }) => (
            <MarkdownCellDiv
              semanticClass="sample-answer"
              text={row.original.answer || ""}
              trimRenderedText
            />
          )
        : ({ getValue }) => <div>{getValue<string>()}</div>,
    });
  }

  // tokens
  cols.push({
    id: "tokens",
    header: "Tokens",
    size: 100,
    minSize: 60,
    maxSize: 140,
    meta: { sortComparator: numberCompare },
    accessorFn: (row) => row.tokens,
    cell: ({ getValue }) => {
      const value = getValue<number | undefined>();
      return value === undefined ? (
        <EmptyCell />
      ) : (
        <div>{formatNumber(value)}</div>
      );
    },
  });

  // cost (hidden by default; undefined when any model went unpriced)
  cols.push({
    id: "cost",
    header: "Cost",
    size: 100,
    minSize: 60,
    maxSize: 140,
    meta: { sortComparator: numberCompare },
    accessorFn: (row) => row.cost,
    cell: ({ getValue }) => {
      const value = getValue<number | undefined>();
      return value === undefined ? (
        <EmptyCell />
      ) : (
        <div>{formatCurrency(value)}</div>
      );
    },
  });

  // duration
  cols.push({
    id: "duration",
    header: "Duration",
    size: 120,
    minSize: 70,
    maxSize: 160,
    meta: { sortComparator: numberCompare },
    accessorFn: (row) => row.duration,
    titleValue: (row) =>
      row.duration === undefined ? undefined : formatTime(row.duration),
    cell: ({ getValue }) => {
      const value = getValue<number | undefined>();
      return value === undefined ? (
        <EmptyCell />
      ) : (
        <div>{formatTime(value)}</div>
      );
    },
  });

  // OPTIONAL columns kept before scores so a scrolling user sees
  // halted/limited/retried-sample signals without scrolling past the
  // (often wide) score-column block.
  cols.push(
    {
      id: "error",
      header: "Error",
      size: 200,
      minSize: 100,
      accessorFn: (row) => row.error ?? row.data?.error ?? "",
      cell: isList
        ? ({ getValue }) => {
            const text = getValue<string>();
            if (!text) return null;
            return (
              <div
                className={clsx(
                  "sample-error",
                  styles.cell,
                  styles.wrapAnywhere,
                  "three-line-clamp"
                )}
              >
                {text}
              </div>
            );
          }
        : ({ getValue }) => <div>{getValue<string>()}</div>,
    },
    {
      id: "limit",
      header: "Limit",
      size: shape ? shape.limitSize * 16 : 100,
      minSize: 28,
      accessorFn: (row) => row.limit ?? row.data?.limit,
      cell: ({ getValue, row }) => (
        <div
          title={
            row.original.limit_reason ??
            row.original.data?.limit_reason ??
            undefined
          }
        >
          {valueAsString(getValue() ?? "")}
        </div>
      ),
    },
    {
      id: "retries",
      header: "Retries",
      size: shape ? shape.retriesSize * 16 : 80,
      minSize: 28,
      meta: { align: "center", sortComparator: numberCompare },
      accessorFn: (row) => row.retries ?? row.data?.retries,
      cell: ({ getValue }) => <div>{valueAsString(getValue() ?? "")}</div>,
    },
    {
      id: "fallbacks",
      header: "Fallbacks",
      // size for the header text — the cell value is just a small count
      size: 95,
      minSize: 28,
      meta: { align: "center", sortComparator: numberCompare },
      accessorFn: (row) => row.fallbacks,
      titleValue: (row) => {
        const lines = modelFallbackLines(row.data?.model_fallbacks);
        return lines.length > 0 ? lines.join("\n") : undefined;
      },
      cell: ({ getValue }) => <div>{valueAsString(getValue() ?? "")}</div>,
    }
  );

  // SCORE COLUMNS
  cols.push(...buildScoreColumns(ctx));

  if (multiLog) {
    cols.push({
      id: "created",
      header: "Eval Created",
      size: 140,
      minSize: 80,
      maxSize: 160,
      meta: { sortComparator: dateCompare },
      accessorFn: (row) => row.created,
      cell: ({ getValue }) => {
        const value = getValue<string | undefined>();
        return <div>{value ? formatDateTime(new Date(value)) : ""}</div>;
      },
    });
  }

  // Mark columns filterable and derive the filter editor type from the sort
  // comparator (mirrors the log list). The status-icon column sorts but has no
  // useful filter editor, and the index column is purely presentational — so
  // both are skipped here.
  for (const col of cols) {
    if (col.id === "sampleStatus" || col.id === "displayIndex") continue;
    const cmp = col.meta?.sortComparator;
    const filterType: FilterType =
      cmp === numberCompare
        ? "number"
        : cmp === dateCompare
          ? "date"
          : "string";
    if (ctx.filterSpecRegistry) {
      const mapping = col.id
        ? ctx.filterSpecRegistry.byColId.get(col.id)
        : undefined;
      if (!mapping) continue; // not representable in filtrex — no funnel
      col.meta = {
        ...col.meta,
        filterable: true,
        filterType,
        operators: samplesOperatorsForKind(mapping.kind),
      };
    } else {
      col.meta = { ...col.meta, filterable: true, filterType };
    }
  }

  return cols;
}

/** Score columns — emitted in one of two modes. */
function buildScoreColumns(ctx: SampleGridContext): SampleColumn[] {
  const {
    descriptor,
    scores,
    samples,
    scoreLabels,
    scoreColorScales,
    compactScores,
  } = ctx;

  // Compact mode: score columns shrink and their headers rotate 45° so many
  // scorers fit horizontally. Numeric scores render a short number (~40px);
  // non-numeric render small pills needing a touch more room (~55px).
  // Returns null when compact is off so each mode keeps its own wide-column
  // defaults.
  const compactSizing = (
    isNumeric: boolean
  ): Pick<SampleColumn, "size" | "minSize"> | null => {
    if (!compactScores) return null;
    const w = isNumeric ? 40 : 55;
    return { size: w, minSize: w - 4 };
  };

  // Resolve a metric name through the eval-author's label overrides,
  // falling back to the raw name. Used for the visible header text;
  // the column `id` is still keyed off the raw name so filter / sort /
  // visibility stay stable across label changes. Own-key reads: score
  // names are log-authored, and "constructor" must not read a builtin.
  const labelFor = (name: string): string => getOwn(scoreLabels, name) ?? name;

  // Build a value→style resolver for the score column whose metric is
  // `name`, given its bounds. Returns undefined when no scale is
  // configured (or it can't be resolved against the bounds), so the cell
  // renders with no background.
  const cellStyleFor = (
    name: string,
    bounds: { min?: number; max?: number }
  ): ((value: unknown) => CSSProperties | undefined) | undefined => {
    const wire = getOwn(scoreColorScales, name);
    if (!wire) return undefined;
    const resolved = resolveScale(wire, bounds);
    if (!resolved) return undefined;
    return (value) => {
      const c = colorForValue(resolved, value);
      return c ? { backgroundColor: c } : undefined;
    };
  };

  // Per-scorer mode (single-log with descriptor). One column per
  // *available* score; visibility is driven by `selectedScores` upstream
  // so the unified column chooser can toggle scorers on and off.
  if (descriptor && scores && scores.length > 0) {
    const useLabelHeader = scores.length !== 1;
    return scores.map((label): SampleColumn => {
      const colId = perScorerFieldKey(label);
      const headerName = useLabelHeader ? labelFor(label.name) : "Score";
      const scoreDesc = descriptor.evalDescriptor.scoreDescriptor(label);
      const scoreType = scoreDesc?.scoreType;
      const isNumeric = scoreType === kScoreTypeNumeric;
      // Pass/fail and boolean already render as semantically-coloured
      // pills via the descriptor; painting a background under them would
      // clash. Only opt the *other* score types into the colour scale.
      const acceptsColorScale =
        scoreType !== kScoreTypePassFail && scoreType !== kScoreTypeBoolean;
      const valueToStyle = acceptsColorScale
        ? cellStyleFor(label.name, {
            min: scoreDesc?.min,
            max: scoreDesc?.max,
          })
        : undefined;
      return {
        id: colId,
        header: headerName,
        ...(compactSizing(isNumeric) ?? {
          size: Math.max(70, Math.round(headerName.length * 6.2) + 40),
          minSize: 60,
          maxSize: 120,
        }),
        meta: {
          align: "center",
          rotateHeader: compactScores,
          sortComparator: isNumeric ? numberCompare : stringCompare,
          cellStyle: valueToStyle
            ? (row) => {
                if (!row.data) return undefined;
                const value = descriptor.evalDescriptor.score(
                  row.data,
                  label
                )?.value;
                return valueToStyle(value);
              }
            : undefined,
        },
        accessorFn: (row) => {
          const data = row.data;
          if (!data) return undefined;
          return descriptor.evalDescriptor.score(data, label)?.value;
        },
        cell: ({ row }) => {
          const item = row.original;
          if (!item.data) return null;
          const completed = item.completed ?? item.data.completed;
          const rendered = descriptor.evalDescriptor
            .score(item.data, label)
            ?.render();
          if (completed && rendered !== undefined) {
            return <ScoreCellDiv>{rendered}</ScoreCellDiv>;
          }
          return <ScoreCellDiv />;
        },
      };
    });
  }

  // Raw mode — discover score names across the supplied samples. Detect
  // type collisions so a name with mixed value types falls back to text.
  // Also tally numeric min/max per column so colour-scale gradients have
  // something to anchor against (no descriptor exists in raw mode).
  // Maps, not plain objects: the names are the log's own score keys.
  const types = new Map<string, Set<string>>();
  const ranges = new Map<string, { min: number; max: number }>();
  for (const sample of samples ?? []) {
    if (!sample.scores) continue;
    for (const [name, score] of Object.entries(sample.scores)) {
      let nameTypes = types.get(name);
      if (!nameTypes) {
        nameTypes = new Set();
        types.set(name, nameTypes);
      }
      nameTypes.add(typeof score.value);
      if (typeof score.value === "number" && Number.isFinite(score.value)) {
        const r = ranges.get(name);
        if (!r) ranges.set(name, { min: score.value, max: score.value });
        else {
          if (score.value < r.min) r.min = score.value;
          if (score.value > r.max) r.max = score.value;
        }
      }
    }
  }
  const scoreNames = [...types.keys()].sort((a, b) => a.localeCompare(b));
  return scoreNames.map((name): SampleColumn => {
    const nameTypes = types.get(name);
    const isUniformNumber = nameTypes?.size === 1 && nameTypes.has("number");
    const valueToStyle = cellStyleFor(name, ranges.get(name) ?? {});
    return {
      id: rawScoreFieldKey(name),
      header: labelFor(name),
      ...(compactSizing(isUniformNumber) ?? { size: 100, minSize: 60 }),
      meta: {
        align: "center",
        rotateHeader: compactScores,
        sortComparator: isUniformNumber ? numberCompare : stringCompare,
        cellStyle: valueToStyle
          ? (row) => valueToStyle(row[rawScoreFieldKey(name)])
          : undefined,
      },
      accessorFn: (row) => row[rawScoreFieldKey(name)],
      cell: ({ getValue }) => {
        const v = getValue<
          string | number | boolean | object | null | undefined
        >();
        if (v === "" || v === null || v === undefined) return <ScoreCellDiv />;
        const text = Array.isArray(v)
          ? v.join(", ")
          : typeof v === "object"
            ? JSON.stringify(v)
            : typeof v === "number"
              ? v.toFixed(3)
              : String(v);
        return <ScoreCellDiv>{text}</ScoreCellDiv>;
      },
    };
  });
}
